import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PrivateBrowsingDetectorService {
  private isPrivateBrowsingCache: boolean | null = null;
  private isIndexedDBStableCache: boolean | null = null;

  /**
   * Detects if the browser is in private browsing mode
   * Works for Safari (iOS/macOS), Chrome, Firefox, Edge, etc.
   */
  async detectPrivateBrowsing(): Promise<boolean> {
    // Return cached result if already detected
    if (this.isPrivateBrowsingCache !== null) {
      return this.isPrivateBrowsingCache;
    }

    try {
      // Method 1: Try to use IndexedDB (Safari private mode restricts this)
      if (!window.indexedDB) {
        this.isPrivateBrowsingCache = true;
        return true;
      }

      // Method 2: Try to open a test database
      const testDBName = '_test_private_browsing_' + Date.now();
      
      return new Promise<boolean>((resolve) => {
        try {
          const openRequest = indexedDB.open(testDBName, 1);
          
          openRequest.onerror = () => {
            // IndexedDB is available but opening failed - likely private mode
            this.isPrivateBrowsingCache = true;
            resolve(true);
          };
          
          openRequest.onsuccess = () => {
            try {
              // Successfully opened - try to create an object store to confirm
              const db = openRequest.result;
              
              // Close and delete the test database
              db.close();
              indexedDB.deleteDatabase(testDBName);
              
              // IndexedDB works fine - not private mode
              this.isPrivateBrowsingCache = false;
              resolve(false);
            } catch (e) {
              // Error during operation - likely private mode
              this.isPrivateBrowsingCache = true;
              resolve(true);
            }
          };

          // Try to create an object store
            openRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
              try {
              const db = (event.target as IDBOpenDBRequest).result;
              // Try to create an object store
              // In private mode, this might fail or db might be undefined/null
              if (!db || typeof db.createObjectStore !== 'function') {
                this.isPrivateBrowsingCache = true;
                resolve(true);
                return;
              }
              
              db.createObjectStore('test');
            } catch (e) {
              // Creating object store failed - definitely private mode
              this.isPrivateBrowsingCache = true;
              resolve(true);
            }
          };
          
          // Timeout fallback
          setTimeout(() => {
            this.isPrivateBrowsingCache = false;
            resolve(false);
          }, 2000);
          
        } catch (e) {
          // Any error means private mode
          this.isPrivateBrowsingCache = true;
          resolve(true);
        }
      });
      
    } catch (e) {
      // IndexedDB not available - assume private mode
      this.isPrivateBrowsingCache = true;
      return true;
    }
  }

  /**
   * Tests IndexedDB stability by attempting to create a database with an object store
   * This specifically tests for the iOS Safari race condition where createObjectStore fails
   * @returns Promise<boolean> - true if stable, false if unstable
   */
  async testIndexedDBStability(): Promise<boolean> {
    // Return cached result if already tested
    if (this.isIndexedDBStableCache !== null) {
      return this.isIndexedDBStableCache;
    }

    try {
      if (!window.indexedDB) {
        this.isIndexedDBStableCache = false;
        return false;
      }

      const testDBName = '_test_indexeddb_stability_' + Date.now();
      
      return new Promise<boolean>((resolve) => {
        let resolved = false;
        let upgradeSuccessful = false;

        const openRequest = indexedDB.open(testDBName, 1);
        
        openRequest.onerror = () => {
          if (!resolved) {
            resolved = true;
            this.isIndexedDBStableCache = false;
            resolve(false);
          }
        };
        
        openRequest.onblocked = () => {
          if (!resolved) {
            resolved = true;
            this.isIndexedDBStableCache = false;
            resolve(false);
          }
        };

        openRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
          try {
            const db = (event.target as IDBOpenDBRequest)?.result;
            
            // CRITICAL: Test for the iOS Safari race condition
            // Sometimes db is undefined/null during onupgradeneeded
            if (!db) {
              if (!resolved) {
                resolved = true;
                this.isIndexedDBStableCache = false;
                resolve(false);
              }
              return;
            }

            // Test if createObjectStore is available
            if (typeof db.createObjectStore !== 'function') {
              if (!resolved) {
                resolved = true;
                this.isIndexedDBStableCache = false;
                resolve(false);
              }
              return;
            }

            // Attempt to create an object store
            db.createObjectStore('_stability_test_store');
            upgradeSuccessful = true;
            
          } catch (e) {
            if (!resolved) {
              resolved = true;
              this.isIndexedDBStableCache = false;
              resolve(false);
            }
          }
        };
        
        openRequest.onsuccess = () => {
          try {
            const db = openRequest.result;
            
            // Verify the upgrade was successful
            if (!upgradeSuccessful) {
              if (!resolved) {
                resolved = true;
                this.isIndexedDBStableCache = false;
                resolve(false);
              }
              return;
            }

            // Clean up
            db.close();
            indexedDB.deleteDatabase(testDBName);
            
            // Success - IndexedDB is stable
            if (!resolved) {
              resolved = true;
              this.isIndexedDBStableCache = true;
              resolve(true);
            }
          } catch (e) {
            if (!resolved) {
              resolved = true;
              this.isIndexedDBStableCache = false;
              resolve(false);
            }
          }
        };
        
        // Timeout fallback - if no response in 3 seconds, consider it unstable
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            this.isIndexedDBStableCache = false;
            
            // Try to clean up
            try {
              if (openRequest.result) {
                openRequest.result.close();
              }
              indexedDB.deleteDatabase(testDBName);
            } catch (e) {
              // Ignore cleanup errors
            }
            
            resolve(false);
          }
        }, 3000);
        
      });
      
    } catch (e) {
      this.isIndexedDBStableCache = false;
      return false;
    }
  }

  /**
   * Synchronously checks if IndexedDB is available
   * This is a quick check but may not be 100% accurate for all browsers
   */
  isIndexedDBAvailable(): boolean {
    try {
      return !!window.indexedDB;
    } catch (e) {
      return false;
    }
  }

  /**
   * Resets the cached private browsing detection result
   */
  resetCache(): void {
    this.isPrivateBrowsingCache = null;
    this.isIndexedDBStableCache = null;
  }
}